<template>
  <div>
    <PageHeader title="Email Templates" buttonLabel="+ New Template" :buttonPath="{ name: 'email-template-create' }" />

    <b-row class="mb-3">
      <b-col cols="12" md="6" lg="4">
        <b-form-input v-model="search" placeholder="Search templates..." @input="onFilterChange" />
      </b-col>
      <b-col cols="12" md="4" lg="3" class="mt-2 mt-md-0">
        <b-form-select v-model="category" :options="categoryOptions" @change="onFilterChange" />
      </b-col>
    </b-row>

    <div v-if="isLoading" class="text-center py-5"><b-spinner /></div>
    <div v-else-if="!templates.length" class="text-center py-5 text-muted">No templates yet.</div>

    <b-row v-else>
      <b-col v-for="t in templates" :key="t.id" sm="6" lg="4" xl="3" class="mb-3">
        <div class="tpl-card card h-100">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-start justify-content-between mb-2">
              <div>
                <i class="material-icons tpl-card-icon">email</i>
              </div>
              <b-dropdown right no-caret variant="link" class="row-action-btn">
                <template #button-content><i class="material-icons">more_vert</i></template>
                <b-dropdown-item :to="{ name: 'email-template-edit', params: { id: t.id } }">
                  <i class="material-icons">edit</i> Edit
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item class="text-danger" @click="removeTemplate(t.id)">
                  <i class="material-icons">delete</i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <router-link :to="{ name: 'email-template-edit', params: { id: t.id } }" class="tpl-card-title">
              {{ t.name }}
            </router-link>
            <div v-if="t.subject" class="tpl-card-subject">{{ t.subject }}</div>
            <div class="mt-auto pt-2">
              <span v-if="t.category" class="tpl-card-type text-capitalize">{{ t.category }}</span>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <div class="d-flex justify-content-center" v-if="totalRows > perPage">
      <Pagination v-model="currentPage" :totalRows="totalRows" :perPage="perPage" @change="onPageChange" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';
import Pagination from '../../components/Ui/Pagination.vue';

export default {
  name: 'TemplatesList',
  components: { PageHeader, Pagination },
  data() {
    return {
      templates: [],
      isLoading: false,
      search: '',
      category: null,
      categoryOptions: [
        { value: null, text: 'All Categories' },
        { value: 'enrollment', text: 'Enrollment' },
        { value: 'marketing', text: 'Marketing' },
        { value: 'follow_up', text: 'Follow Up' },
        { value: 'general', text: 'General' },
      ],
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
    };
  },
  methods: {
    ...mapActions('emailTemplate', ['getAllEmailTemplates', 'deleteEmailTemplate']),
    onFilterChange() {
      this.currentPage = 1;
      this.fetchTemplates();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchTemplates();
    },
    async fetchTemplates() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
        };
        if (this.search) params.search = this.search;
        if (this.category) params.category = this.category;
        const res = await this.getAllEmailTemplates(params);
        const data = res.results !== undefined ? res : (res.data || {});
        this.templates = data.results || [];
        this.totalRows = data.count || this.templates.length;
      } catch (err) {
        this.makeToast('danger', 'Failed to load templates');
      } finally {
        this.isLoading = false;
      }
    },
    async removeTemplate(id) {
      if (!confirm('Delete this template?')) return;
      try {
        await this.deleteEmailTemplate(id);
        this.makeToast('success', 'Template deleted');
        this.fetchTemplates();
      } catch (err) {
        this.makeToast('danger', 'Failed to delete template');
      }
    },
  },
  created() { this.fetchTemplates(); },
};
</script>

<style scoped>
.tpl-card { transition: box-shadow 0.15s; }
.tpl-card:hover { box-shadow: 0 0.25rem 2rem rgba(58,59,69,0.2); }
.tpl-card-icon { font-size: 28px; color: #868e96; }
.tpl-card-title {
  font-weight: 600; font-size: 0.95rem; color: #272C33;
  text-decoration: none; line-height: 1.4;
}
.tpl-card-title:hover { color: var(--primary-color); }
.tpl-card-subject {
  font-size: 0.8rem; color: #868e96; margin-top: 0.25rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.tpl-card-type { font-size: 0.75rem; color: #868e96; }
</style>
